<template>
  <div class="booking site" role="dialog" aria-modal="true" :aria-label="`Contact ${member.name}`" @keydown.esc="close">
    <div ref="scrim" class="booking__scrim" @click="close"></div>
    <div ref="card" class="booking__card">
      <button ref="closeBtn" v-magnetic="0.3" class="booking__close" type="button" aria-label="Cerrar" @click="close">
        <span class="booking__close-icon" aria-hidden="true"></span>
      </button>
      <div class="booking__media">
        <img :src="member.image_url" :alt="member.name">
      </div>
      <form class="booking__body" novalidate @submit.prevent="send">
        <p class="eyebrow" data-booking-in>Booking request</p>
        <h3 ref="title" class="booking__name">{{ member.name }}</h3>
        <p class="booking__text" data-booking-in>
          Leave your email and we'll get back to you about dates, fees and availability.
        </p>
        <label class="booking__field" data-booking-in>
          <span class="booking__label">Your e-mail</span>
          <input
            ref="input"
            v-model.trim="email"
            type="email"
            placeholder="your@email.com"
            autocomplete="email"
            :disabled="status === 'sending' || status === 'sent'"
            required
          >
        </label>
        <div class="booking__actions" data-booking-in>
          <button class="pill pill--solid" type="submit" :disabled="status === 'sending' || status === 'sent'">
            {{ buttonLabel }}
            <span class="arrow" aria-hidden="true">↗</span>
          </button>
          <p class="booking__status" :class="`is-${status}`" aria-live="polite">{{ statusText }}</p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { gsap, SplitText, prefersReducedMotion } from '@/animations/gsap'
import { lockScroll, unlockScroll } from '@/animations/smoothScroll'
import { apiUrl } from '@/services/apiConfig'

const EMAIL = /[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w](?:[\w-]*[\w])?\.)+[\w](?:[\w-]*[\w])?/

export default {
  name: 'BookingModal',
  props: {
    member: { type: Object, required: true },
    // Punto (en viewport) desde donde se abre el círculo.
    origin: { type: Object, default: null }
  },
  data() {
    return { email: '', status: 'idle' }
  },
  computed: {
    buttonLabel() {
      if (this.status === 'sending') return 'Sending'
      if (this.status === 'sent') return 'Sent'
      return 'Send'
    },
    statusText() {
      if (this.status === 'invalid') return 'Please enter a valid e-mail.'
      if (this.status === 'error') return 'Mail could not be sent. Please try again.'
      if (this.status === 'sent') return 'Mail sent successfully!'
      return ''
    }
  },
  mounted() {
    document.body.appendChild(this.$el)
    lockScroll()
    this.previousFocus = document.activeElement
    this.$nextTick(this.open)
  },
  beforeDestroy() {
    if (this.timeline) this.timeline.kill()
    if (this.split) this.split.revert()
    unlockScroll()
    if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
    if (this.previousFocus && this.previousFocus.focus) this.previousFocus.focus({ preventScroll: true })
  },
  methods: {
    open() {
      const { card, scrim, title, input } = this.$refs
      if (prefersReducedMotion()) {
        gsap.fromTo([scrim, card], { opacity: 0 }, { opacity: 1, duration: 0.25 })
        input.focus({ preventScroll: true })
        return
      }
      const rect = card.getBoundingClientRect()
      const o = this.origin || { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      const local = { x: o.x - rect.left, y: o.y - rect.top }
      const radius = Math.hypot(Math.max(local.x, rect.width - local.x), Math.max(local.y, rect.height - local.y))
      const circle = (r) => `circle(${r}px at ${local.x}px ${local.y}px)`

      this.split = SplitText.create(title, { type: 'words,chars', mask: 'chars' })
      this.timeline = gsap.timeline({ onComplete: () => input.focus({ preventScroll: true }) })
        .fromTo(scrim, { opacity: 0 }, { opacity: 1, duration: 0.8, ease: 'power2.out' }, 0)
        .fromTo(card,
          { clipPath: circle(0), rotation: local.x > rect.width / 2 ? -4 : 4, scale: 0.94 },
          { clipPath: circle(radius), rotation: 0, scale: 1, duration: 1.2, ease: 'expo.out', clearProps: 'clipPath' },
          0.05)
        .from(card.querySelector('.booking__media img'), { scale: 1.3, duration: 1.6, ease: 'expo.out' }, 0.05)
        .from(this.split.chars, { yPercent: 110, duration: 1, stagger: 0.022, ease: 'expo.out' }, 0.3)
        .from(card.querySelectorAll('[data-booking-in]'), { y: 24, opacity: 0, duration: 0.9, stagger: 0.06, ease: 'expo.out' }, 0.4)
    },
    close() {
      if (this.closing) return
      this.closing = true
      if (this.timeline) this.timeline.kill()
      const done = () => this.$emit('close')
      if (prefersReducedMotion()) {
        done()
        return
      }
      gsap.timeline({ onComplete: done })
        .to(this.$refs.card, { y: 40, opacity: 0, duration: 0.45, ease: 'power2.in' }, 0)
        .to(this.$refs.scrim, { opacity: 0, duration: 0.5, ease: 'power2.in' }, 0.1)
    },
    async send() {
      if (!EMAIL.test(this.email)) {
        this.status = 'invalid'
        return
      }
      this.status = 'sending'
      try {
        const response = await fetch(apiUrl('/send'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: this.member.email,
            message: `Hola, te contacto desde la web de COPPADOS. Mi email es: ${this.email}`
          })
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        this.status = 'sent'
        setTimeout(this.close, 1800)
      } catch (error) {
        console.error('Error sending booking mail:', error)
        this.status = 'error'
      }
    }
  }
}
</script>

<style scoped>
.booking {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: grid;
  place-items: center;
  padding: var(--gutter);
}

.booking__scrim {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
}

.booking__card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1fr);
  width: min(62rem, 100%);
  max-height: calc(100svh - 2 * var(--gutter));
  background: var(--c-surface);
  border: 1px solid var(--c-line);
  overflow: hidden;
}

.booking__close {
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 2;
  width: 3rem;
  height: 3rem;
  display: grid;
  place-items: center;
  border: 1px solid var(--c-line);
  border-radius: 50%;
  color: #fff;
  transition: background-color 0.4s var(--ease-out), border-color 0.4s var(--ease-out);
}

.booking__close-icon {
  position: relative;
  width: 1rem;
  height: 1rem;
  transition: transform 0.6s var(--ease-out);
}

.booking__close-icon::before,
.booking__close-icon::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 1px;
  background: currentColor;
}

.booking__close-icon::before {
  transform: rotate(45deg);
}

.booking__close-icon::after {
  transform: rotate(-45deg);
}

@media (hover: hover) {
  .booking__close:hover {
    background: var(--c-accent);
    border-color: var(--c-accent);
  }

  .booking__close:hover .booking__close-icon {
    transform: rotate(90deg);
  }
}

.booking__media {
  position: relative;
  overflow: hidden;
  min-height: 22rem;
  background: #111;
}

.booking__media img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.booking__body {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 1.25rem;
  padding: clamp(2rem, 1rem + 3vw, 3.5rem);
  padding-top: 4rem;
}

.booking__name {
  color: #fff;
  font-size: clamp(2.4rem, 1.2rem + 3.5vw, 4.5rem);
  line-height: 0.9;
  font-weight: 400;
  letter-spacing: -0.02em;
  text-transform: uppercase;
}

.booking__text {
  color: var(--c-muted);
  font-size: var(--fs-body);
  line-height: 1.45;
  max-width: 36ch;
}

.booking__field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.booking__label {
  font-size: var(--fs-small);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--c-muted);
}

.booking__field input {
  width: 100%;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--c-line);
  padding: 0.75rem 0;
  color: #fff;
  font-size: var(--fs-lead);
  outline: none;
  transition: border-color 0.4s var(--ease-out);
}

.booking__field input:focus {
  border-color: #fff;
}

.booking__field input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.booking__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
}

.booking__actions .pill[disabled] {
  opacity: 0.6;
  pointer-events: none;
}

.booking__status {
  font-size: 0.85rem;
  color: var(--c-muted);
}

.booking__status.is-sent {
  color: #fff;
}

.booking__status.is-error,
.booking__status.is-invalid {
  color: var(--c-accent);
}

@media (max-width: 700px) {
  .booking {
    align-items: end;
    padding: 0;
  }

  .booking__card {
    grid-template-columns: 1fr;
    max-height: 92svh;
    overflow-y: auto;
  }

  .booking__media {
    min-height: 0;
    aspect-ratio: 16 / 10;
  }

  .booking__body {
    padding-top: 2rem;
  }
}
</style>
